// features/tutorials/components/SearchOverlay.tsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, BookOpen, ArrowRight, Clock } from 'lucide-react';
import type { TutorialModule, TutorialLesson } from '../types';

interface SearchOverlayProps {
    modules: TutorialModule[];
    onClose: () => void;
    onLessonSelect: (lesson: TutorialLesson) => void;
}

export const SearchOverlay: React.FC<SearchOverlayProps> = ({
    modules,
    onClose,
    onLessonSelect,
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<Array<{ lesson: TutorialLesson; module: TutorialModule }>>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        if (query.trim()) {
            const searchTerm = query.toLowerCase();
            const allResults: Array<{ lesson: TutorialLesson; module: TutorialModule }> = [];

            modules.forEach((module: TutorialModule) => {
                module.lessons.forEach((lesson: TutorialLesson) => {
                    if (
                        lesson.title.toLowerCase().includes(searchTerm) ||
                        lesson.description.toLowerCase().includes(searchTerm) ||
                        lesson.steps.some((step) =>
                            step.content.toLowerCase().includes(searchTerm) ||
                            step.title.toLowerCase().includes(searchTerm)
                        ) ||
                        lesson.tips.some((tip) => tip.toLowerCase().includes(searchTerm))
                    ) {
                        allResults.push({ lesson, module });
                    }
                });
            });

            setResults(allResults);
            setSelectedIndex(0);
        } else {
            setResults([]);
        }
    }, [query, modules]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex((prev: number) => Math.min(prev + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex((prev: number) => Math.max(prev - 1, 0));
            } else if (e.key === 'Enter' && results[selectedIndex]) {
                onLessonSelect(results[selectedIndex].lesson);
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [results, selectedIndex, onLessonSelect, onClose]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
        >
            <div className="min-h-full flex items-start justify-center pt-20 px-4">
                <motion.div
                    initial={{ scale: 0.95, opacity: 0, y: -20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.95, opacity: 0, y: -20 }}
                    transition={{ type: 'spring', damping: 30 }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    className="w-full max-w-2xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden"
                >
                    {/* Search Input */}
                    <div className="p-4 border-b border-slate-200 dark:border-slate-700">
                        <div className="flex items-center space-x-3">
                            <Search className="w-5 h-5 text-slate-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                placeholder="Search tutorials, features, or questions..."
                                value={query}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                                className="flex-1 bg-transparent text-slate-900 dark:text-white placeholder-slate-400 outline-none text-lg"
                            />
                            <button
                                onClick={onClose}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <X className="w-5 h-5 text-slate-400" />
                            </button>
                        </div>
                    </div>

                    {/* Results */}
                    <div className="max-h-96 overflow-y-auto">
                        <AnimatePresence>
                            {results.length > 0 ? (
                                <div className="py-2">
                                    {results.map((result, index: number) => (
                                        <motion.button
                                            key={result.lesson.id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.03 }}
                                            onClick={() => onLessonSelect(result.lesson)}
                                            className={`w-full p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${index === selectedIndex ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                                }`}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <BookOpen className="w-5 h-5 text-slate-400" />
                                                <div className="text-left">
                                                    <div className="font-medium text-slate-900 dark:text-white">
                                                        {result.lesson.title}
                                                    </div>
                                                    <div className="text-sm text-slate-500 dark:text-slate-400">
                                                        {result.module.title} • {result.lesson.description}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <span className="flex items-center text-sm text-slate-400">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    {result.lesson.duration}
                                                </span>
                                                <ArrowRight className="w-4 h-4 text-slate-400" />
                                            </div>
                                        </motion.button>
                                    ))}
                                </div>
                            ) : query ? (
                                <div className="p-8 text-center">
                                    <p className="text-slate-500 dark:text-slate-400 mb-2">
                                        No results found for "{query}"
                                    </p>
                                    <p className="text-sm text-slate-400">
                                        Try different keywords or browse the modules
                                    </p>
                                </div>
                            ) : (
                                <div className="p-8 text-center">
                                    <p className="text-slate-500 dark:text-slate-400 mb-2">
                                        Start typing to search all lessons
                                    </p>
                                    <p className="text-sm text-slate-400">
                                        Search by topic, feature name, or keywords
                                    </p>
                                </div>
                            )}
                        </AnimatePresence>
                    </div>
                </motion.div>
            </div>
        </motion.div>
    );
};